
import React, { useState, useMemo } from 'react';
import { AppState } from '../types.ts';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid } from 'recharts';
import { Calendar, SearchX, Activity, Zap, TrendingUp, PlusCircle } from 'lucide-react';
import AddBetModal from './AddBetModal.tsx';

interface StatsViewProps {
  state: AppState;
  onAddBet: (bet: any) => void;
}

const StatsView: React.FC<StatsViewProps> = ({ state, onAddBet }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  const filteredBets = useMemo(() => {
    return state.bets.filter(bet => bet.date >= startDate && bet.date <= endDate);
  }, [state.bets, startDate, endDate]);

  const playerStats = useMemo(() => {
    return state.players
      .map(player => {
        const p1Bets = filteredBets.filter(b => b.player1_id === player.id);
        const p2Bets = filteredBets.filter(b => b.player2_id === player.id);
        const allBets = [...p1Bets, ...p2Bets];
        const profit = allBets.reduce((acc, b) => acc + (b.profit || 0), 0);

        const profitPerEntry = allBets.length > 0 ? profit / allBets.length : 0;
        const goalsMade = p1Bets.reduce((acc, b) => acc + (b.player1_goals || 0), 0) + p2Bets.reduce((acc, b) => acc + (b.player2_goals || 0), 0);

        return {
          name: player.name.toUpperCase(),
          profit,
          count: allBets.length,
          avgGoalsMade: allBets.length > 0 ? goalsMade / allBets.length : 0,
          profitPerEntry
        };
      })
      .filter(p => p.count > 0).sort((a, b) => b.profit - a.profit);
  }, [state.players, filteredBets]);

  const formatDateDisplay = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  return (
    <div className="space-y-8 flex flex-col min-h-full">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h2 className="text-3xl font-black uppercase tracking-tight text-white flex items-center gap-3">
            <Activity className="text-[#00ff66]" /> Analytics Pro
          </h2>
          <p className="text-[#848e9c] text-xs font-bold mt-1 uppercase tracking-widest">Análise de Performance e ROI por Entrada</p>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-3 bg-[#1e2329] border border-[#474d57] px-5 py-3 rounded-2xl text-[11px] font-black text-white shadow-xl relative overflow-hidden group">
            <label
              className="flex items-center gap-2 relative h-6 min-w-[110px] justify-center cursor-pointer"
              onClick={(e) => (e.currentTarget.querySelector('input') as HTMLInputElement)?.showPicker?.()}
            >
              <Calendar size={16} className="text-[#00ff66] pointer-events-none" />
              <span className="text-white pointer-events-none tracking-widest">{formatDateDisplay(startDate)}</span>
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
              />
            </label>
            <span className="text-white opacity-40 pointer-events-none uppercase tracking-widest">até</span>
            <label
              className="flex items-center gap-2 relative h-6 min-w-[110px] justify-center cursor-pointer"
              onClick={(e) => (e.currentTarget.querySelector('input') as HTMLInputElement)?.showPicker?.()}
            >
              <Calendar size={16} className="text-[#00ff66] pointer-events-none" />
              <span className="text-white pointer-events-none tracking-widest">{formatDateDisplay(endDate)}</span>
              <input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
              />
            </label>
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-[#00ff66] text-[#0b0e11] font-black px-6 py-3.5 rounded-xl flex items-center justify-center gap-2 hover:bg-[#0bb574] transition-all shadow-[0_0_20px_rgba(0,255,102,0.3)] active:scale-95 uppercase text-[11px] tracking-widest"
          >
            <PlusCircle size={18} /> Nova Entrada
          </button>
        </div>
      </div>

      {filteredBets.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 pb-4">
          <div className="bg-[#1e2329] p-8 rounded-[32px] border border-[#474d57] shadow-xl">
            <h3 className="text-sm font-black text-white uppercase tracking-widest mb-10 border-b border-[#474d57] pb-4 flex items-center gap-2">
              <TrendingUp size={16} className="text-[#00ff66]" /> Ranking Financeiro Players
            </h3>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={playerStats.slice(0, 10)} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2b3139" horizontal={true} vertical={false} />
                  <XAxis type="number" hide />
                  <YAxis dataKey="name" type="category" stroke="#ffffff" fontSize={10} width={100} fontWeight="bold" />
                  <Tooltip
                    cursor={{ fill: '#2b3139', opacity: 0.5 }}
                    contentStyle={{ backgroundColor: '#1e2329', border: '1px solid #00ff66', borderRadius: '16px', color: '#ffffff' }}
                    itemStyle={{ color: '#ffffff' }}
                    labelStyle={{ color: '#00ff66', fontWeight: 'bold', marginBottom: '4px' }}
                  />
                  <Bar dataKey="profit" radius={[0, 10, 10, 0]} barSize={24}>
                    {playerStats.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.profit >= 0 ? '#00ff66' : '#f6465d'} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-[#1e2329] p-8 rounded-[32px] border border-[#474d57] shadow-xl">
            <h3 className="text-sm font-black text-white uppercase tracking-widest mb-10 border-b border-[#474d57] pb-4 flex items-center gap-2">
              <Zap size={16} className="text-[#fbbf24]" /> Eficiência por Entrada ($)
            </h3>
            <div className="space-y-4">
              {playerStats.slice(0, 6).map((p, i) => (
                <div key={i} className="p-5 bg-[#0b0e11] rounded-2xl border border-[#474d57]/30 flex items-center justify-between group hover:border-[#00ff66] transition-all">
                  <div>
                    <p className="font-black text-white uppercase text-sm">{p.name}</p>
                    <p className="text-[10px] font-bold text-[#848e9c] uppercase tracking-widest mt-0.5">{p.count} Jogos Realizados</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[9px] font-black text-[#848e9c] uppercase tracking-widest">ROI / Entrada</p>
                    <p className={`text-xl font-black ${p.profitPerEntry >= 0 ? 'text-[#00ff66]' : 'text-[#f6465d]'}`}>${p.profitPerEntry.toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-40 bg-[#1e2329]/30 rounded-[48px] border border-[#474d57]/30">
          <SearchX size={64} className="text-[#474d57]" />
          <h3 className="text-white font-black uppercase mt-4">Sem dados operacionais no período</h3>
        </div>
      )}

      <AddBetModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onAdd={onAddBet} state={state} />
    </div>
  );
};

export default StatsView;
